import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './LeadSources.module.css';

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const LeadSources = () => {
  const navigate = useNavigate();
  const [leadSources, setLeadSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false);
  const [filters, setFilters] = useState({
    search: ''
  });
  const [dataTableConfig, setDataTableConfig] = useState({
    start: 0,
    length: 30,
    currentPage: 1
  });
  const [columnVisibility, setColumnVisibility] = useState({
    name: true,
    actions: true
  });
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const columnVisibilityRef = useRef(null);

  // State for table sorting
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  useEffect(() => {
    loadLeadSources();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnVisibilityRef.current && !columnVisibilityRef.current.contains(event.target)) {
        setShowColumnVisibility(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadLeadSources = async () => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${baseUrl}/api/leadsources`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setLeadSources(data.data);
          setUseMockData(false);
        } else {
          throw new Error('Invalid response format');
        }
      } else {
        throw new Error('Failed to load lead sources');
      }
    } catch (error) {
      console.error('Error loading lead sources:', error);
      setUseMockData(true);
      setLeadSources(getMockLeadSources());
      toast.warning('Using mock data due to backend connection issue');
    } finally {
      setLoading(false);
    }
  };

  // Mock data for fallback
  const getMockLeadSources = () => [
    { id: 1, name: 'ACT-ON' },
    { id: 2, name: 'Advertising Boards' },
    { id: 3, name: 'Anthony Laville\'s Property Meet Up' },
    { id: 4, name: 'Assets & Estates Management Conference 2017' },
    { id: 5, name: 'Assets and Estate Management 2018' },
    { id: 6, name: 'B.E Norwich' },
    { id: 7, name: 'Built Environment Networking - Leicester' },
    { id: 8, name: 'Campaign Monitor' },
    { id: 9, name: 'Canvassing' },
    { id: 10, name: 'CIH Brighton 2025' },
    { id: 11, name: 'CIH Brighton Conference May 2022' },
    { id: 12, name: 'CIH Housing 2019' },
    { id: 13, name: 'CIH housing 22' },
    { id: 14, name: 'CIH Manchester 2025' },
    { id: 15, name: 'CoStar' },
    { id: 16, name: 'Costar email Oct 2019' },
    { id: 17, name: 'CURRENT CLIENT REFERRAL' },
    { id: 18, name: 'Dementia and Care Home Expo' },
    { id: 19, name: 'DEVELOP CROYDON' },
    { id: 20, name: 'DEVELOPMENT SUMMIT' },
    { id: 21, name: 'DOMINIC SLATTER' },
    { id: 22, name: 'EG AWARDS 2017' },
    { id: 23, name: 'ENHANCING CROYDON' },
    { id: 24, name: 'EREN ELMAZ' },
    { id: 25, name: 'ESTATE GAZETTE' },
    { id: 26, name: 'Future of Housing 2019' },
    { id: 27, name: 'Google' },
    { id: 28, name: 'LinkedIn' },
    { id: 29, name: 'Property Portal' },
    { id: 30, name: 'Referral' }
  ];

  const handleAddLeadSource = () => {
    navigate('/lead-sources/create');
  };

  const handleEditLeadSource = (id) => {
    navigate(`/lead-sources/${id}/edit`);
  };

  const handleDeleteLeadSource = async (id) => {
    if (window.confirm('Are you sure you want to delete this lead source?')) {
      try {
        if (!useMockData) {
          const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
          const response = await fetch(`${baseUrl}/api/leadsources/${id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.ok) {
            setLeadSources(leadSources.filter(source => source.id !== id));
            toast.success('Lead source deleted successfully!');
          } else {
            throw new Error('Failed to delete lead source');
          }
        } else {
          // Mock deletion
          setLeadSources(leadSources.filter(source => source.id !== id));
          toast.success('Lead source deleted successfully! (Mock)');
        }
      } catch (error) {
        console.error('Error deleting lead source:', error);
        toast.error('Failed to delete lead source');
      }
    }
  };

  const handleExportCSV = async () => {
    try {
      if (!useMockData) {
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${baseUrl}/api/leadsources/export/csv`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'lead_sources.csv';
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.success('CSV exported successfully!');
        } else {
          throw new Error('Failed to export CSV');
        }
      } else {
        // Mock CSV export
        const csvContent = [
          'ID,Name',
          ...filteredLeadSources.map(source => `${source.id},"${source.name}"`)
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'lead_sources.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('CSV exported successfully! (Mock)');
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const handleExportPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(18);
      doc.text('Lead Sources Report', 20, 20);
      
      // Add date
      doc.setFontSize(12);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
      
      // Add table
      doc.setFontSize(10);
      let yPosition = 50;
      
      // Table header
      doc.setFont(undefined, 'bold');
      doc.text('ID', 20, yPosition);
      doc.text('Name', 40, yPosition);
      yPosition += 10;
      
      // Table data
      doc.setFont(undefined, 'normal');
      filteredLeadSources.forEach((item, index) => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(item.id.toString(), 20, yPosition);
        doc.text(item.name, 40, yPosition);
        yPosition += 8;
      });
      
      // Save the PDF
      doc.save('lead_sources_report.pdf');
      toast.success('PDF exported successfully!');
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  const handlePrintTable = () => {
    window.print();
  };

  const toggleColumnVisibility = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  const toggleColumn = (columnKey) => {
    console.log('Toggling column:', columnKey);
    setColumnVisibility(prev => {
      const newState = {
        ...prev,
        [columnKey]: !prev[columnKey]
      };
      console.log('New column visibility state:', newState);
      return newState;
    });
  };

  const handlePageSizeChange = (newSize) => {
    setDataTableConfig(prev => ({
      ...prev,
      length: newSize,
      currentPage: 1,
      start: 0
    }));
  };

  const handlePageChange = (page) => {
    const start = (page - 1) * dataTableConfig.length;
    setDataTableConfig(prev => ({
      ...prev,
      currentPage: page,
      start: start
    }));
  };

  const handleSearchChange = (e) => {
    setFilters(prev => ({
      ...prev,
      search: e.target.value
    }));
    setDataTableConfig(prev => ({
      ...prev,
      currentPage: 1,
      start: 0
    }));
  };

  // Handle table sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Filter and sort lead sources
  const filteredLeadSources = leadSources
    .filter(source => {
      const matchesSearch = source.name.toLowerCase().includes(filters.search.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested object sorting - map sortField to actual data field names
      if (sortField === "name") {
        aValue = a.name || "";
        bValue = b.name || "";
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  // Pagination
  const totalPages = Math.ceil(filteredLeadSources.length / dataTableConfig.length);
  const startIndex = dataTableConfig.start;
  const endIndex = Math.min(startIndex + dataTableConfig.length, filteredLeadSources.length);
  const currentLeadSources = filteredLeadSources.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <p>Loading lead sources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <nav className={styles.breadcrumb}>
        <ol>
          <li>
            <a href="/dashboard">Dashboard</a>
          </li>
          <li>
            <a href="/clients">Clients</a>
          </li>
          <li className={styles.active}>Lead Sources</li>
        </ol>
      </nav>

      {/* Content Area with Background */}
      <div className={styles.contentArea}>
        {/* Page Title */}
        <div className={styles.pageTitle}>
          <h1>Lead Sources</h1>
          <button 
            className={styles.addButton}
            onClick={handleAddLeadSource}
          >
            <i className={styles.plusIcon}>+</i> Add Lead Source
          </button>
        </div>



        {/* Table Controls */}
        <div className={styles.tableControls}>
          <div className={styles.leftControls}>
            <div className={styles.entriesControl}>
              <label>
                Show{' '}
                <select
                  value={dataTableConfig.length}
                  onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                  className={styles.entriesSelect}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                </select>{' '}
                entries
              </label>
            </div>
            <div className={styles.tableSearch}>
              <label>
                Search:{' '}
                <input
                  type="search"
                  className={styles.searchInput}
                  value={filters.search}
                  onChange={handleSearchChange}
                  placeholder="Search lead sources..."
                />
              </label>
            </div>
          </div>
          <div className={styles.exportButtons}>
            <button className={styles.exportButton} onClick={handleExportCSV}>
              Export as CSV
            </button>
            <button className={styles.exportButton} onClick={handleExportPDF}>
              Export as PDF
            </button>
            <button className={styles.exportButton} onClick={handlePrintTable}>
              Print table
            </button>
            <div className={styles.columnVisibilityContainer} ref={columnVisibilityRef}>
              <button 
                className={styles.exportButton} 
                onClick={toggleColumnVisibility}
              >
                Column visibility
              </button>
              {showColumnVisibility && (
                <div className={styles.columnVisibilityDropdown}>
                  <div className={styles.columnVisibilityOptions}>
                    <label className={styles.columnVisibilityOption}>
                      <input
                        type="checkbox"
                        checked={columnVisibility.name}
                        onChange={() => toggleColumn('name')}
                      />
                      <span>Name</span>
                    </label>
                    <label className={styles.columnVisibilityOption}>
                      <input
                        type="checkbox"
                        checked={columnVisibility.actions}
                        onChange={() => toggleColumn('actions')}
                      />
                      <span>Actions</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className={styles.tableWrapper}>
          <table className={styles.leadSourcesTable}>
            <thead>
              <tr>
                {columnVisibility.name && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("name")}
                    style={{ cursor: "pointer" }}
                  >
                    Name<SortIcon direction={sortField === "name" ? sortDirection : null} isActive={sortField === "name"} />
                  </th>
                )}
                {columnVisibility.actions && <th className={styles.actionsCell}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {currentLeadSources.map((leadSource) => (
                <tr key={leadSource.id}>
                  {columnVisibility.name && <td>{leadSource.name}</td>}
                  {columnVisibility.actions && (
                    <td className={styles.actionsCell}>
                      <button
                        className={`${styles.actionButton} ${styles.editButton}`}
                        onClick={() => handleEditLeadSource(leadSource.id)}
                      >
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.paginationWrapper}>
            <div className={styles.paginationInfo}>
              Showing {startIndex + 1} to {endIndex} of {filteredLeadSources.length} entries
            </div>
            <div className={styles.pagination}>
              <button
                className={`${styles.paginationButton} ${dataTableConfig.currentPage === 1 ? styles.disabled : ''}`}
                onClick={() => handlePageChange(dataTableConfig.currentPage - 1)}
                disabled={dataTableConfig.currentPage === 1}
              >
                Previous
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`${styles.paginationButton} ${dataTableConfig.currentPage === page ? styles.active : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
              
              <button
                className={`${styles.paginationButton} ${dataTableConfig.currentPage === totalPages ? styles.disabled : ''}`}
                onClick={() => handlePageChange(dataTableConfig.currentPage + 1)}
                disabled={dataTableConfig.currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {leadSources.length === 0 && !loading && (
          <div className={styles.noData}>
            <p>No lead sources found!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadSources; 